import {
  Button,
  Modal,
  Typography,
  Upload,
  message,
  Form,
  Select,
  Checkbox,
  Progress,
  Alert,
} from "antd";
import { RcFile, UploadFile } from "antd/es/upload";
import { InboxOutlined } from "@ant-design/icons";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useGetPresignedUrl } from "~/services/upload";
import { CreateFileDto, useCreateFile } from "~/services/file";
import { DocumentStatus, DocumentType } from "~/utils/constants";
import { TBooking } from "~/types";

const { Text } = Typography;
const { Option } = Select;
const { Dragger } = Upload;

interface DocumentFile extends UploadFile {
  originFileObj?: RcFile;
}

interface DocumentFormValues {
  type: DocumentType;
  visibleToStaff: boolean;
  visibleToClient: boolean;
}

interface BookingDocumentUploadModalProps {
  visible: boolean;
  booking: TBooking;
  onClose: () => void;
  onSuccess: () => void;
}

export const BookingDocumentUploadModal: React.FC<
  BookingDocumentUploadModalProps
> = ({ visible, booking, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<DocumentFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);

  const getPresignedUrlMutation = useGetPresignedUrl();
  const createFileMutation = useCreateFile();

  useEffect(() => {
    if (visible) {
      form.setFieldsValue({
        type: DocumentType.INVENTORY,
        visibleToStaff: true,
        visibleToClient: false,
      });
    }
  }, [visible, form]);

  const validTypes = [
    "application/pdf",
    "image/jpeg",
    "image/png",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ];

  const MAX_FILE_SIZE_MB = 15;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
  const MAX_TOTAL_SIZE_MB = 50;
  const MAX_TOTAL_SIZE_BYTES = MAX_TOTAL_SIZE_MB * 1024 * 1024;

  const getTotalSize = (files: DocumentFile[]) =>
    files.reduce((sum, f) => sum + (f.originFileObj?.size || f.size || 0), 0);

  const handleUploadChange = ({ fileList: newFileList }: any) => {
    // Filter valid file types and enforce per-file size limit
    const filteredFiles = newFileList.filter((file: any) => {
      // Skip already uploaded files
      if (file.status === 'done') return true;

      const isValidType = validTypes.includes(file.type);
      if (!isValidType && file.status !== 'error') {
        message.error(`${file.name} is not a valid file type`);
        return false;
      }

      const fileSize = file.originFileObj?.size || file.size || 0;
      if (fileSize > MAX_FILE_SIZE_BYTES) {
        message.error(`${file.name} exceeds ${MAX_FILE_SIZE_MB}MB limit`);
        return false;
      }

      return isValidType;
    });

    // Check total size cap
    const totalSize = getTotalSize(filteredFiles);
    if (totalSize > MAX_TOTAL_SIZE_BYTES) {
      message.error(`Total upload size exceeds ${MAX_TOTAL_SIZE_MB}MB. Please remove some files.`);
      return;
    }

    setFileList(filteredFiles);
  };

  const handleRemove = (file: UploadFile) => {
    setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
  };

  const getFileTitle = (fileName: string): string => {
    // Remove extension and clean up the filename
    return fileName.replace(/\.[^/.]+$/, "").replace(/[-_]/g, " ");
  };

  const handleSubmit = async (values: DocumentFormValues) => {
    if (fileList.length === 0) {
      message.error("Please select at least one document to upload");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setCurrentFileIndex(0);

    const totalFiles = fileList.length;
    let successCount = 0;
    let failCount = 0;

    try {
      for (let i = 0; i < fileList.length; i++) {
        const fileItem = fileList[i];
        const file = fileItem.originFileObj;

        if (!file) {
          failCount++;
          continue;
        }

        setCurrentFileIndex(i + 1);

        try {
          // Step 1: Get presigned URL from server
          const presignedUrlData = await getPresignedUrlMutation.mutateAsync({
            fileName: file.name,
            mimeType: file.type,
          });

          // Step 2: Upload file directly to storage using presigned URL
          await axios.put(presignedUrlData.presignedUrl, file, {
            headers: {
              "Content-Type": file.type,
            },
          });

          // Step 3: Create file record in database with the uploaded file URL
          const fileData: CreateFileDto = {
            title: getFileTitle(file.name),
            type: values.type,
            status: DocumentStatus.SENT,
            visibleToStaff: values.visibleToStaff ?? true,
            visibleToClient: values.visibleToClient ?? false,
            filePath: presignedUrlData.url,
            jobTicketId: booking.id,
            clientId: booking.clientId,
          };

          await createFileMutation.mutateAsync(fileData);
          successCount++;
        } catch (err) {
          console.error(`Error uploading ${file.name}:`, err);
          failCount++;
        }

        setUploadProgress(Math.round(((i + 1) / totalFiles) * 100));
      }

      if (successCount > 0) {
        message.success(
          `${successCount} document${successCount > 1 ? "s" : ""} uploaded successfully${
            failCount > 0 ? ` (${failCount} failed)` : ""
          }`
        );
        setFileList([]);
        form.resetFields();
        onSuccess();
        onClose();
      } else {
        message.error("Failed to upload documents. Please try again.");
      }
    } catch (error) {
      console.error("Error uploading documents:", error);
      message.error("Failed to upload documents. Please try again.");
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setCurrentFileIndex(0);
    }
  };

  const handleCancel = () => {
    if (isUploading) return;
    setFileList([]);
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title="Upload Documents"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={600}
      maskClosable={!isUploading}
      closable={!isUploading}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          type: DocumentType.INVENTORY,
          visibleToStaff: true,
          visibleToClient: false,
        }}
      >
        <Form.Item
          label="Document Files"
          required
        >
          <Dragger
            multiple
            beforeUpload={() => false}
            onChange={handleUploadChange}
            onRemove={handleRemove}
            fileList={fileList}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            disabled={isUploading}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Click or drag files to this area to upload
            </p>
            <p className="ant-upload-hint">
              Support for multiple files. PDF, DOCX, JPG, PNG (Max: {MAX_FILE_SIZE_MB}MB each, {MAX_TOTAL_SIZE_MB}MB total)
            </p>
          </Dragger>
        </Form.Item>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            name="type"
            label="Document Type"
            rules={[{ required: true }]}
            extra="Applied to all files"
          >
            <Select disabled={isUploading}>
              <Option value={DocumentType.INVENTORY}>Inventory</Option>
              <Option value={DocumentType.ADMINISTRATIVE}>Administrative</Option>
              <Option value={DocumentType.LEGAL}>Legal</Option>
              <Option value={DocumentType.SUPPLEMENTARY}>Supplementary</Option>
              <Option value={DocumentType.OTHER}>Other</Option>
            </Select>
          </Form.Item>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            name="visibleToStaff"
            valuePropName="checked"
            label="Staff Visibility"
            extra="Applied to all files"
          >
            <Checkbox disabled={isUploading}>Visible to Staff</Checkbox>
          </Form.Item>

          <Form.Item
            name="visibleToClient"
            valuePropName="checked"
            label="Client Visibility"
            extra="Applied to all files"
          >
            <Checkbox disabled={isUploading}>Visible to Client</Checkbox>
          </Form.Item>
        </div>

        <Form.Item noStyle shouldUpdate={(prev, cur) => prev.visibleToClient !== cur.visibleToClient}>
          {({ getFieldValue }) =>
            getFieldValue("visibleToClient") ? (
              <Alert
                message="Clients will be able to view this document once uploaded."
                type="warning"
                showIcon
                className="mb-4"
              />
            ) : null
          }
        </Form.Item>

        {isUploading && (
          <div className="mb-4">
            <Text className="text-sm text-gray-600">
              Uploading file {currentFileIndex} of {fileList.length}...
            </Text>
            <Progress percent={uploadProgress} status="active" />
          </div>
        )}

        <Form.Item className="mb-0">
          <div className="flex justify-end space-x-2">
            <Button onClick={handleCancel} disabled={isUploading}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={isUploading}
              disabled={fileList.length === 0}
            >
              Upload {fileList.length > 0 ? `(${fileList.length})` : ""}
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};
